type LogLevel = "error" | "warn" | "info" | "debug";

interface LogData {
  message: string;
  data?: any;
  error?: Error;
}

class SimpleLogger {
  private isDevelopment: boolean;

  constructor() {
    this.isDevelopment = process.env.NODE_ENV === "development";
  }

  private log(level: LogLevel, logData: LogData): void {
    if (!this.isDevelopment && level !== "error") {
      return; // Only log errors in production
    }

    const timestamp = new Date().toISOString();

    const logMessage = {
      timestamp,
      level,
      message: logData.message,
      ...(logData.data && { data: logData.data }),
      ...(logData.error && {
        error: { message: logData.error.message, stack: logData.error.stack },
      }),
    };

    switch (level) {
      case "error":
        console.error(`[${timestamp}] ERROR:`, logMessage);
        break;
      case "warn":
        console.warn(`[${timestamp}] WARN:`, logMessage);
        break;
      case "info":
        console.info(`[${timestamp}] INFO:`, logMessage);
        break;
      case "debug":
        console.debug(`[${timestamp}] DEBUG:`, logMessage);
        break;
    }
  }

  error(message: string, data?: any, error?: Error): void {
    this.log("error", { message, data, error });
  }

  warn(message: string, data?: any): void {
    this.log("warn", { message, data });
  }

  info(message: string, data?: any): void {
    this.log("info", { message, data });
  }

  debug(message: string, data?: any): void {
    this.log("debug", { message, data });
  }

  // API specific logging methods
  apiRequest(endpoint: string, method: string, data?: any): void {
    this.debug(`API Request: ${method} ${endpoint}`, data);
  }

  apiResponse(endpoint: string, status: number, data?: any): void {
    this.debug(`API Response: ${endpoint} [${status}]`, data);
  }

  apiError(endpoint: string, error: Error, data?: any): void {
    this.error(`API Error: ${endpoint}`, data, error);
  }
}

export const logger = new SimpleLogger();
export default logger;
